import { Box, Typography, Stack, Alert, CircularProgress } from '@mui/material'
import type React from 'react'

import { useGetUnableToVerifyIncomeResultQuery } from '@/lib/apollo'
import type { OutcomeStatus } from '@/types/Outcome'

import type { UnableToVerifyReason } from './constants'
import DecisionComments from './DecisionComments'
import IncomeOutcomeCard from './IncomeOutcomeCard'

interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  incomeId: string
  userId: string
  reason?: UnableToVerifyReason
  incomeType: string
  incomeStatus: string
  companyName: string
  mandatedTrustedSourcesEnabled?: boolean
}

export const PreviewStep: React.FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  incomeId,
  userId,
  reason,
  incomeType,
  incomeStatus,
  companyName,
  mandatedTrustedSourcesEnabled,
}) => {
  const { loading, error, data } = useGetUnableToVerifyIncomeResultQuery({
    variables: {
      customerId,
      applicationId,
      subjectId,
      incomeId,
      userId,
      reason,
    },
    fetchPolicy: 'network-only',
    onError: () =>
      console.error(
        `Unable to verify income result failed for subjectId ${subjectId} and incomeId ${incomeId}`,
        { subjectId, incomeId },
      ),
  })

  if (error) {
    return (
      <Alert severity="error" sx={{ fontSize: '14px' }}>
        {error.message ||
          'Unable to load the income verification preview. Please try again.'}
      </Alert>
    )
  }

  if (loading || !data?.getUnableToVerifyIncomeResult) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
        <CircularProgress size={32} sx={{ color: '#1B4554' }} />
        <Typography variant="body2" color="text.secondary">
          Loading preview. This may take a few minutes.
        </Typography>
      </Stack>
    )
  }

  const { totalAmountVerified, outcomeStatus, decisionComments } =
    data.getUnableToVerifyIncomeResult

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <IncomeOutcomeCard
        outcomeStatus={outcomeStatus as OutcomeStatus}
        totalAmountVerified={totalAmountVerified}
        incomeType={incomeType}
        incomeStatus={incomeStatus}
        companyName={companyName}
        reason={mandatedTrustedSourcesEnabled ? reason : undefined}
        showTrustedSource={mandatedTrustedSourcesEnabled}
      />

      <DecisionComments comments={decisionComments ?? []} />
    </Box>
  )
}

export default PreviewStep
